import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1464708042292-1323f62d4e3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const signup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (signup) {
        if (password !== confirm) throw new Error("Passwords do not match");
        const { data } = await api.post("/auth/register", { name, email, password });
        setDone(data.message);
        setMode("signin");
        setPassword("");
        setConfirm("");
      } else {
        await login(email, password);
        toast.success("Welcome back");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const switchMode = () => { setMode(signup ? "signin" : "signup"); setError(""); setDone(""); };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      <div className="relative hidden lg:block bg-slate-950 overflow-hidden">
        <img src={HERO} alt="Fleet" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex items-center rounded-xl bg-white px-4 py-3 shadow-lg self-start" data-testid="login-hero-logo">
            <img src="/ekdant-logo-transparent.png" alt="Ekdant Tours & Travels" className="h-14 w-auto" />
          </div>
          <div className="max-w-lg">
            <div className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-bold mb-4">Business &amp; Financial ERP</div>
            <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
              Every duty, every kilometre, every rupee — in one ledger.
            </h1>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Corporate duties, own fleet, market vehicles, GST invoicing and MIS — built for operations across
              Chhatrapati Sambhajinagar, Nashik, Shirdi &amp; Jalna.
            </p>
          </div>
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} Ekdant Tours and Travels</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden mb-10" data-testid="login-mobile-logo">
            <img src="/ekdant-logo-transparent.png" alt="Ekdant Tours & Travels" className="h-16 w-auto dark:bg-white dark:rounded-lg dark:px-2" />
          </div>
          <div className="eyebrow mb-2">{signup ? "New staff account" : "Secure Access"}</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight" data-testid="auth-heading">{signup ? "Create account" : "Sign in"}</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-8">{signup ? "Staff accounts need admin approval before they can sign in." : "Use your credentials to continue."}</p>
          {done && (
            <div className="text-sm text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-md px-3 py-2 mb-4" data-testid="signup-success">{done}</div>
          )}
          <div className="space-y-4">
            {signup && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 text-base" data-testid="signup-name-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{signup ? "Email" : "Email or mobile (drivers)"}</Label>
              <Input id="email" type={signup ? "email" : "text"} required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 text-base" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required autoComplete={signup ? "new-password" : "current-password"} value={password}
                onChange={(e) => setPassword(e.target.value)} className="h-11 text-base" data-testid="login-password-input" />
            </div>
            {signup && (
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 text-base" data-testid="signup-confirm-input" />
              </div>
            )}
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full h-11 group" data-testid="login-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{signup ? "Create account" : "Sign in"} <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" /></>}
            </Button>
            <div className="text-sm text-center text-muted-foreground">
              {signup ? "Already have an account?" : "New staff member?"}{" "}
              <button type="button" onClick={switchMode} className="font-semibold text-amber-600 hover:underline" data-testid="toggle-auth-mode-btn">
                {signup ? "Sign in" : "Create an account"}
              </button>
            </div>
          </div>
          <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Encrypted session · Account locks after 5 failed attempts
          </div>
        </form>
      </div>
    </div>
  );
}
